/**
 * API Response Helpers
 * 
 * Standardized functions to extract data from API responses.
 * All pages should use these helpers for consistent data handling.
 */

/**
 * Extract licenses array from /api/licenses response
 * Response structure: { success: true, data: { licenses: [...], pagination: {...} } }
 * After apiClient: { licenses: [...], pagination: {...} }
 */
export const extractLicenses = (response) => {
  if (!response) return [];
  
  // Handle different response structures
  const licensesArray = response.licenses || response.data?.licenses || response;
  
  return Array.isArray(licensesArray) ? licensesArray : [];
};

/**
 * Extract requests array from /api/license-requests/mine response
 * Response structure: { success: true, data: { requests: [...] } }
 * After apiClient: { requests: [...] }
 */
export const extractRequests = (response) => {
  if (!response) return [];
  
  // Handle different response structures
  const requestsArray = response.requests || response.data?.requests || response;
  
  return Array.isArray(requestsArray) ? requestsArray : [];
};

/**
 * Extract activation keys array from /api/activation-keys/my-keys response
 * Response structure: Direct array [...]
 * After apiClient: [...] (unchanged)
 */
export const extractActivationKeys = (response) => {
  if (!response) return [];
  
  // This endpoint returns direct array
  return Array.isArray(response) ? response : [];
};

/**
 * Extract cluster licenses array from /api/cluster-licenses/my-licenses response
 * Response structure: Direct array [...]
 * After apiClient: [...] (unchanged)
 */
export const extractClusterLicenses = (response) => {
  if (!response) return [];
  
  // This endpoint returns direct array
  return Array.isArray(response) ? response : [];
};

/**
 * Extract users array from /api/users response
 * Response structure: { success: true, data: { users: [...], pagination: {...} } }
 * After apiClient: { users: [...], pagination: {...} }
 */
export const extractUsers = (response) => {
  if (!response) return [];
  
  const usersArray = response.users || response.data?.users || response;
  
  return Array.isArray(usersArray) ? usersArray : [];
};

/**
 * Extract products array from /api/products response
 */
export const extractProducts = (response) => {
  if (!response) return [];
  
  const productsArray = response.products || response.data?.products || response;
  
  return Array.isArray(productsArray) ? productsArray : [];
};

/**
 * Safe filter for Promise.allSettled results
 * Extracts value from fulfilled promises or returns fallback
 */
export const extractFromSettled = (settledResult, extractorFn = (v) => v, fallback = []) => {
  if (settledResult.status === 'fulfilled') {
    return extractorFn(settledResult.value) || fallback;
  }
  return fallback;
};
